import { useState, useCallback, useRef, useEffect, useMemo, lazy, Suspense, type ChangeEvent } from "react";
import ScriptEditor, { type ScriptEditorRef } from "./editor/ScriptEditor";
import Viewport, { type ViewportHandle, type MeshHitInfo } from "./renderer/Viewport";
import ParamPanel from "./params/ParamPanel";
import VersionPanel from "./plm/VersionPanel";
import MotionStudyPanel from "./plm/MotionStudyPanel";
import AiAdvisorPanel from "./ai/AiAdvisorPanel";
import { useAnnotation3DStore } from "./annotations/useAnnotation3DStore";
import Annotation3DOverlay from "./annotations/Annotation3DOverlay";
import BendTablePanel from "./features/BendTablePanel";
import ReleasePanel from "./plm/ReleasePanel";
import BOMPanel from "./plm/BOMPanel";
import ECOPanel from "./plm/ECOPanel";
import DiffViewer from "./plm/DiffViewer";
import DrawingCanvas from "./drawing/DrawingCanvas";
import DesignPanel from "./features/DesignPanel";
import { useFeatureStore } from "./features/useFeatureStore";
import ErrorBoundary from "./ui/ErrorBoundary";
import PartPicker from "./features/PartPicker";
import { useModal } from "./ui/Modal";
import { ScriptRunner, type MeshResult, type FeatureMeshMap, type ScriptRunnerStatus, type ParamMeta } from "./engine/scriptRunner";
import { saveScript, loadScript } from "./engine/autosave";
import { export3MF } from "./engine/threemfExport";
import { setDocId, yText, yFeatures, ydoc } from "./collab/yjsDoc";
import { getHeadId } from "./plm/versionGraph";
import type { Feature, ImportedPartFeature, MateType } from "./features/featureTypes";
import { setPresence, colorForName, setCameraState, getCameraState } from "./collab/presence";
import PresenceAvatars from "./ui/PresenceAvatars";
import NotificationBell from "./ui/NotificationBell";
import ThemeToggle from "./ui/ThemeToggle";
const QRCodeCanvas = lazy(() =>
  import("qrcode.react").then((m) => ({ default: m.QRCodeCanvas }))
);
import { showToast, ToastContainer } from "./ui/Toast";
import { startNotificationPolling } from "./ui/browserNotify";
import HelpModal from "./ui/HelpModal";
import ManageAccess from "./ui/ManageAccess";
import "./App.css";

type ShareExpiry = "30d" | "90d" | "180d" | "1y" | "forever";
const SHARE_EXPIRY_OPTIONS: { value: ShareExpiry; label: string }[] = [
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "180d", label: "180 days" },
  { value: "1y", label: "1 year" },
  { value: "forever", label: "Forever" },
];

interface AppProps {
  documentId: string;
  documentName: string;
  onBackToList: () => void;
}

export default function App({ documentId, documentName, onBackToList }: AppProps) {
  const modal = useModal();
  const [mesh, setMesh] = useState<MeshResult | null>(null);
  const [status, setStatus] = useState<ScriptRunnerStatus>("loading");
  const [errorMsg, setErrorMsg] = useState<string>();
  const scriptEditorRef = useRef<ScriptEditorRef>(null);
  const [paramsMeta, setParamsMeta] = useState<ParamMeta[]>([]);
  const [paramValues, setParamValues] = useState<Record<string, number>>({});
  const [featureMeshes, setFeatureMeshes] = useState<FeatureMeshMap | null>(null);
  const [vertexCount, setVertexCount] = useState(0);
  const [faceCount, setFaceCount] = useState(0);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showManageAccess, setShowManageAccess] = useState(false);
  const [shareExpiry, setShareExpiry] = useState<ShareExpiry>("30d");
  const shareMenuRef = useRef<HTMLDivElement>(null);

  const [isDragOver, setIsDragOver] = useState(false);
  const [savedAt, setSavedAt] = useState<string>("");
  const runnerRef = useRef<ScriptRunner | null>(null);
  const lastScriptRef = useRef<string>("");
  const [initialScript, setInitialScript] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<"gui" | "script">("gui");
  type SidebarTab = "design" | "script" | "ai" | "versions" | "motion" | "release" | "bom" | "eco";
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>("design");
  const [showDrawing, setShowDrawing] = useState(false);
  const [docType, setDocType] = useState("part");
  const [docPartNumber, setDocPartNumber] = useState("");
  const [docRevision, setDocRevision] = useState("A");
  const [diffState, setDiffState] = useState<{
    original: string;
    modified: string;
    originalLabel: string;
    modifiedLabel: string;
  } | null>(null);

  // Initialize Yjs doc for this document BEFORE hooks subscribe.
  // Must be synchronous so useFeatureStore subscribes to the right Y.Array.
  const thumbnailCapturedRef = useRef(false);
  const prevDocIdRef = useRef<string | null>(null);
  if (prevDocIdRef.current !== documentId) {
    prevDocIdRef.current = documentId;
    setDocId(documentId);
    thumbnailCapturedRef.current = false;
  }

  // GUI mode: feature store generates display script (for Script tab) and
  // execution script (with __featureShapes for per-feature mesh separation)
  const handleGuiScript = useCallback((displayScript: string, execScript: string) => {
    if (displayScript === lastScriptRef.current) return;
    lastScriptRef.current = displayScript;
    ydoc.transact(() => {
      yText.delete(0, yText.length);
      yText.insert(0, displayScript);
    });
    setParamValues({});
    runnerRef.current?.execute(execScript).catch(() => {});
    saveScript(displayScript).then(() => {
      setSavedAt(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    });
  }, []);

  const featureStore = useFeatureStore(handleGuiScript, documentId);

  const handleDetachToScript = useCallback(async () => {
    if (await modal.confirm("Detach to script mode? You won't be able to return to GUI mode for this document.", { destructive: true })) {
      setEditMode("script");
      setSidebarTab("script");
    }
  }, [modal]);

  useEffect(() => {
    const cleanup = startNotificationPolling();
    return cleanup;
  }, []);

  useEffect(() => {
    if (!showShareMenu) return;
    const onMouse = (e: MouseEvent) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(e.target as Node)) {
        setShowShareMenu(false);
      }
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setShowShareMenu(false); };
    window.addEventListener("mousedown", onMouse);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onMouse);
      window.removeEventListener("keydown", onKey);
    };
  }, [showShareMenu]);

  // ? key opens help modal
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "?" && !["INPUT", "TEXTAREA", "SELECT"].includes((e.target as HTMLElement)?.tagName)) {
        setShowHelp(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    const name = documentName || "User";
    setPresence({ name, color: colorForName(name) });
    // Fetch document type for BOM panel
    fetch(`/api/documents/${documentId}`, { credentials: "include" })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data?.doc_type) setDocType(data.doc_type);
        if (data?.part_number) setDocPartNumber(data.part_number);
        if (data?.revision) setDocRevision(data.revision);
      })
      .catch(() => {});
  }, [documentId, documentName]);

  useEffect(() => {
    // Check if a sample script was loaded for this document
    const sampleKey = `sample-script-${documentId}`;
    const sampleScript = sessionStorage.getItem(sampleKey);
    if (sampleScript) {
      sessionStorage.removeItem(sampleKey);
      setInitialScript(sampleScript);
      setEditMode("script");
      setSidebarTab("script");
      // Inject into Yjs text
      ydoc.transact(() => {
        yText.delete(0, yText.length);
        yText.insert(0, sampleScript);
      });
      return;
    }
    loadScript().then((saved) => {
      if (saved) setInitialScript(saved);
      else setInitialScript("");
    });
  }, []);

  // Auto-capture thumbnail after first successful geometry render per session
  useEffect(() => {
    if (status !== "idle" || !mesh || thumbnailCapturedRef.current) return;
    thumbnailCapturedRef.current = true;
    const timer = setTimeout(async () => {
      try {
        const blob = await viewportRef.current?.exportImage(0.5);
        if (!blob) return;
        await fetch(`/api/documents/${documentId}/thumbnail`, {
          method: "POST",
          headers: { "Content-Type": "image/png" },
          body: blob,
          credentials: "include",
        });
      } catch { /* non-blocking — thumbnail is best-effort */ }
    }, 800);
    return () => clearTimeout(timer);
  }, [status, mesh, documentId]);

  useEffect(() => {
    const runner = new ScriptRunner(
      (s, msg) => {
        setStatus(s);
        if (s === "error") setErrorMsg(msg);
        else setErrorMsg(undefined);
      },
      (m, fm) => {
        if (!m) { setMesh(null); setFeatureMeshes(null); setVertexCount(0); setFaceCount(0); return; }
        setMesh(m);
        setFeatureMeshes(fm ?? null);
        setVertexCount(m.vertices.length / 3);
        setFaceCount(m.indices.length / 3);
      },
      (params) => setParamsMeta(params)
    );
    runnerRef.current = runner;
    return () => runner.dispose();
  }, []);

  const handleExecute = useCallback((script: string) => {
    lastScriptRef.current = script;
    setParamValues({});
    runnerRef.current?.execute(script).catch(() => {});
    saveScript(script).then(() => {
      setSavedAt(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    });
  }, []);

  const handleParamChange = useCallback((name: string, value: number) => {
    const next = { ...paramValues, [name]: value };
    setParamValues(next);
    runnerRef.current?.execute(lastScriptRef.current, next).catch(() => {});
  }, [paramValues]);

  const handleStepFile = useCallback((file: File) => {
    runnerRef.current?.importStep(file).catch(() => {});
  }, []);

  const triggerDownload = useCallback((blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  const handleExportSTL = useCallback(() => {
    runnerRef.current?.exportShape("stl").then((blob) => {
      triggerDownload(blob, "export.stl");
      showToast("STL exported", "success");
    }).catch(() => {});
  }, [triggerDownload]);

  const handleExportSTEP = useCallback(() => {
    runnerRef.current?.exportShape("step").then((blob) => {
      triggerDownload(blob, "export.step");
      showToast("STEP exported", "success");
    }).catch(() => {});
  }, [triggerDownload]);

  const handleExportPNG = useCallback(async () => {
    try {
      const blob = await viewportRef.current?.exportImage(2);
      if (blob) {
        triggerDownload(blob, `${documentName || "export"}.png`);
        showToast("PNG exported", "success");
      }
    } catch { showToast("Export failed", "error"); }
  }, [triggerDownload, documentName]);

  const handleExport3MF = useCallback(async () => {
    if (!mesh) { showToast("No geometry to export", "error"); return; }
    try {
      const blob = await export3MF(mesh, documentName || "export");
      triggerDownload(blob, `${documentName || "export"}.3mf`);
      showToast("3MF exported", "success");
    } catch { showToast("3MF export failed", "error"); }
  }, [mesh, triggerDownload, documentName]);

  const handleExportGLB = useCallback(async () => {
    try {
      const blob = await viewportRef.current?.exportGLB();
      if (blob) {
        triggerDownload(blob, `${documentName || "export"}.glb`);
        showToast("GLB exported", "success");
      }
    } catch { showToast("Export failed", "error"); }
  }, [triggerDownload, documentName]);

  const handleMeshSelect = useCallback((featureId: string | null) => {
    if (featureId) {
      // "__last" = single-mesh fallback (no per-feature IDs available)
      const id = featureId === "__last"
        ? featureStore.features.filter((f) => f.enabled).at(-1)?.id ?? null
        : featureId;
      featureStore.selectFeature(id);
      setTimeout(() => {
        document.querySelector(".feature-tree-item.selected")?.scrollIntoView({ block: "nearest" });
      }, 50);
    } else {
      featureStore.selectFeature(null);
    }
  }, [featureStore.selectFeature, featureStore.features]);

  const [showPartPicker, setShowPartPicker] = useState(false);
  const [mateMode, setMateMode] = useState(false);
  const [mateSource, setMateSource] = useState<MeshHitInfo | null>(null);
  const [mateType, setMateType] = useState<MateType>("faceToFace");
  const [commentMode, setCommentMode] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [followClientId, setFollowClientId] = useState<number | null>(null);
  const annotationStore = useAnnotation3DStore();
  const [explodeScale, setExplodeScale] = useState(0);
  const [sectionEnabled, setSectionEnabled] = useState(false);
  const [sectionAxis, setSectionAxis] = useState<"X" | "Y" | "Z">("X");
  const [sectionPosition, setSectionPosition] = useState(0);

  const explodeOffsets = useMemo(() => {
    const map: Record<string, [number, number, number]> = {};
    for (const f of featureStore.features) {
      if (f.type === "importedPart") {
        const ip = f as ImportedPartFeature;
        if (ip.explosionOffset) map[f.id] = ip.explosionOffset;
      }
    }
    return Object.keys(map).length > 0 ? map : undefined;
  }, [featureStore.features]);

  const featureMaterials = useMemo(() => {
    const map: Record<string, string> = {};
    for (const f of featureStore.features) {
      if (f.materialPreset && f.materialPreset !== "default") map[f.id] = f.materialPreset;
    }
    return Object.keys(map).length > 0 ? map : undefined;
  }, [featureStore.features]);

  const handleMeshHit = useCallback((hit: MeshHitInfo | null) => {
    if (!hit) return;
    // Comment mode: place annotation at click point
    if (commentMode) {
      const text = window.prompt("Add comment:");
      if (text?.trim()) {
        annotationStore.addAnnotation(hit.point, text.trim(), documentName || "User", hit.featureId);
        showToast("Comment added", "success");
      }
      return;
    }
    if (!mateMode) return;
    if (!mateSource) {
      setMateSource(hit);
      showToast("Source face selected — choose mate type, then click target", "info");
    } else {
      featureStore.applyMate(
        mateType,
        hit.featureId,
        mateSource.point, mateSource.normal,
        hit.point, hit.normal,
        mateSource.featureId,
      );
      setMateMode(false);
      setMateSource(null);
      showToast("Mate applied", "success");
    }
  }, [mateMode, mateSource, mateType, featureStore, commentMode, annotationStore, documentName]);

  const handlePartPicked = useCallback(async (part: import("./features/PartPicker").PartPickerResult) => {
    setShowPartPicker(false);
    // Standard parts provide script inline — no server fetch needed
    if (part.script) {
      featureStore.addImportedPart(part.id, part.name, part.script, part.configuration);
      showToast(`Inserted ${part.name}`, "success");
      return;
    }
    try {
      const scriptRes = await fetch(`/api/documents/${part.id}/script`);
      const script = scriptRes.ok ? await scriptRes.text() : undefined;
      featureStore.addImportedPart(part.id, part.name, script);
      fetch(`/api/bom/${documentId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ child_doc_id: part.id, quantity: 1 }),
      }).catch(() => {});
      showToast(`Imported ${part.name}`, "success");
    } catch {
      featureStore.addImportedPart(part.id, part.name);
      showToast(`Imported ${part.name} (script unavailable)`, "info");
    }
  }, [documentId, featureStore]);

  const getCurrentScript = useCallback(() => {
    return yText.toString();
  }, []);

  const getCurrentFeatures = useCallback(() => {
    return JSON.stringify(yFeatures.toArray());
  }, []);

  const handleVersionCheckout = useCallback((script: string, features?: string) => {
    ydoc.transact(() => {
      yText.delete(0, yText.length);
      yText.insert(0, script);
      // Restore feature tree if available
      if (features) {
        try {
          const parsed = JSON.parse(features) as Feature[];
          yFeatures.delete(0, yFeatures.length);
          yFeatures.push(parsed);
        } catch { /* skip corrupt feature data */ }
      }
    });
    handleExecute(script);
    const head = getHeadId();
    showToast(`Checked out version ${head ? head.slice(0, 7) : ""}`.trim(), "info");
  }, [handleExecute]);

  const handleCompare = useCallback(
    (original: string, modified: string, originalLabel: string, modifiedLabel: string) => {
      setDiffState({ original, modified, originalLabel, modifiedLabel });
    },
    []
  );

  const viewportRef = useRef<ViewportHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="app-shell">
      {/* ── Top Bar ─────────────────────────────────────────── */}
      <div className="topbar">
        <div className="topbar-brand">
          <button
            className="toolbar-btn"
            onClick={onBackToList}
            title="Back to documents"
            style={{ marginRight: 4 }}
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M10 3L5 8l5 5"/>
            </svg>
          </button>
          <div className="topbar-logo">
            <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 9L6 2L10 9H2Z" stroke="white" strokeWidth="1.5" strokeLinejoin="round"/>
            </svg>
          </div>
          <span className="topbar-title">{documentName}</span>
        </div>

        <div className="toolbar-group">
          <button className="toolbar-btn-label" onClick={() => fileInputRef.current?.click()} title="Import STEP file">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><path d="M8 2v8M5 7l3 3 3-3M3 12h10"/></svg>
            Import
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".step,.stp"
            className="hidden"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const f = e.target.files?.[0];
              if (f) handleStepFile(f);
              e.target.value = "";
            }}
          />
          <button className="toolbar-btn-label" onClick={handleExportSTL} title="Export STL">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><path d="M8 14V6M5 9l3-3 3 3M3 4h10"/></svg>
            STL
          </button>
          <button className="toolbar-btn-label" onClick={handleExportPNG} title="Export high-res PNG">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><rect x="2" y="2" width="12" height="10" rx="1"/><circle cx="5" cy="7" r="1.5"/><path d="M14 12l-3-4-2 2-2-3-4 5"/></svg>
            PNG
          </button>
          <button className="toolbar-btn-label" onClick={handleExportSTEP} title="Export STEP">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><path d="M8 14V6M5 9l3-3 3 3M3 4h10"/></svg>
            STEP
          </button>
          <button className="toolbar-btn-label" onClick={handleExportGLB} title="Export GLB (web 3D)">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><path d="M3 5l5-3 5 3v6l-5 3-5-3V5z"/><path d="M3 5l5 3 5-3M8 8v6"/></svg>
            GLB
          </button>
          <button className="toolbar-btn-label" onClick={handleExport3MF} title="Export 3MF (3D printing)">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><path d="M4 12V4l4-2 4 2v8l-4 2-4-2z"/><path d="M4 4l4 2 4-2M8 6v8"/></svg>
            3MF
          </button>
          <button
            className={`toolbar-btn-label ${showDrawing ? "active" : ""}`}
            onClick={() => setShowDrawing(!showDrawing)}
            title="Toggle 2D Drawing"
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><rect x="2" y="2" width="12" height="12" rx="1"/><path d="M2 10h12M8 2v8"/></svg>
            Drawing
          </button>
        </div>

        <div className="toolbar-group">
          <button className="toolbar-btn" title="Fit to view (F)" onClick={() => viewportRef.current?.fitToView()}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><rect x="2" y="2" width="12" height="12" rx="1"/><path d="M5 5h6v6H5z" strokeDasharray="2 1"/></svg>
          </button>
          <button className="toolbar-btn" title="Toggle grid" onClick={() => viewportRef.current?.toggleGrid()}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><path d="M2 5.5h12M2 10.5h12M5.5 2v12M10.5 2v12"/></svg>
          </button>
          <button className="toolbar-btn" title="Toggle edges" onClick={() => viewportRef.current?.toggleEdges()}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><path d="M3 12L8 3l5 9H3z"/><circle cx="8" cy="8" r="3"/></svg>
          </button>
          <button className={`toolbar-btn ${sectionEnabled ? "active" : ""}`} title="Section view" onClick={() => setSectionEnabled(!sectionEnabled)}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><path d="M8 2v12" strokeDasharray="2 1.5"/><rect x="2" y="4" width="12" height="8" rx="1"/></svg>
          </button>
          {sectionEnabled && (
            <>
              <select className="toolbar-select" value={sectionAxis} onChange={(e) => setSectionAxis(e.target.value as "X" | "Y" | "Z")} title="Section axis">
                <option value="X">X</option><option value="Y">Y</option><option value="Z">Z</option>
              </select>
              <input type="range" className="toolbar-slider" min={-100} max={100} value={sectionPosition} onChange={(e) => setSectionPosition(Number(e.target.value))} title={`Section position: ${sectionPosition}`} />
            </>
          )}
          {docType === "assembly" && (
            <button className={`toolbar-btn ${explodeScale > 0 ? "active" : ""}`} title="Explode view" onClick={() => setExplodeScale(explodeScale > 0 ? 0 : 1)}>
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><path d="M8 2l-2 3M8 2l2 3M8 14l-2-3M8 14l2-3M2 8l3-2M2 8l3 2M14 8l-3-2M14 8l-3 2"/></svg>
            </button>
          )}
          <button className={`toolbar-btn ${commentMode ? "active" : ""}`} title="Add 3D comment" onClick={() => setCommentMode(!commentMode)}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><path d="M3 3h10a1 1 0 011 1v6a1 1 0 01-1 1H6l-3 3V4a1 1 0 011-1z"/></svg>
          </button>
          <button className="toolbar-btn" title="Help (?)" onClick={() => setShowHelp(true)}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="6"/><path d="M6 6.5a2 2 0 013.5 1.5c0 1-1.5 1-1.5 2M8 12.5v0"/></svg>
          </button>
        </div>

        <div className="topbar-spacer" />

        <div style={{ position: "relative" }} ref={shareMenuRef}>
          <button
            className={`toolbar-btn ${showShareMenu ? "active" : ""}`}
            title="Share document"
            onClick={() => setShowShareMenu(!showShareMenu)}
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M6.5 9.5a3.5 3.5 0 0 0 5 0l2-2a3.5 3.5 0 0 0-5-5L7 4"/>
              <path d="M9.5 6.5a3.5 3.5 0 0 0-5 0l-2 2a3.5 3.5 0 0 0 5 5L9 12"/>
            </svg>
          </button>
          {showShareMenu && (
            <div className="share-menu">
              <div className="share-menu-header">Share</div>
              <button className="share-menu-item" onClick={() => {
                navigator.clipboard.writeText(window.location.href)
                  .then(() => { showToast("Link copied", "success"); setShowShareMenu(false); })
                  .catch(() => showToast("Failed to copy link", "error"));
              }}>
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
                  <rect x="3" y="3" width="7" height="7" rx="1"/>
                  <path d="M6 6h7v7H6"/>
                </svg>
                Copy Link
              </button>
              <div className="share-qr-container">
                <Suspense fallback={<div style={{ width: 140, height: 140 }} />}>
                  <QRCodeCanvas value={window.location.href} size={140} marginSize={4} />
                </Suspense>
              </div>
              <div className="share-menu-divider" />
              <div className="share-menu-label">Public Link</div>
              <div className="share-expiry-row">
                <select
                  className="share-expiry-select"
                  value={shareExpiry}
                  onChange={(e) => setShareExpiry(e.target.value as ShareExpiry)}
                >
                  {SHARE_EXPIRY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <button className="share-menu-item share-create-btn" onClick={async () => {
                  const expiry = shareExpiry;
                  try {
                    const res = await fetch(`/api/documents/${documentId}/share-token`, {
                      method: "POST", credentials: "include",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ expiry }),
                    });
                    if (!res.ok) throw new Error("Failed to create share link");
                    const data = await res.json();
                    await navigator.clipboard.writeText(data.url);
                    const label = expiry === "forever" ? "never expires" : `expires in ${expiry}`;
                    showToast(`Public link copied (${label})`, "success");
                  } catch { showToast("Failed to create public link", "error"); }
                  setShowShareMenu(false);
                }}>
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
                    <circle cx="8" cy="8" r="6"/>
                    <path d="M2 8h12M8 2a10 10 0 0 1 3 6 10 10 0 0 1-3 6 10 10 0 0 1-3-6 10 10 0 0 1 3-6"/>
                  </svg>
                  Create &amp; Copy
                </button>
              </div>
              <button className="share-menu-item" onClick={async () => {
                try {
                  const res = await fetch(`/api/huly/documents/${documentId}/share`, {
                    method: "POST", credentials: "include",
                  });
                  if (!res.ok) {
                    const body = await res.json().catch(() => ({}));
                    throw new Error(body.error || "Failed");
                  }
                  const data = await res.json();
                  showToast("Shared to Huly", "success");
                  if (data.huly_url) window.open(data.huly_url, "_blank");
                } catch (err: unknown) { showToast(err instanceof Error ? err.message : "Huly share failed", "error"); }
                setShowShareMenu(false);
              }}>
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
                  <path d="M4 12V4l8 4-8 4z"/>
                </svg>
                Share to Huly
              </button>
              <div className="share-menu-divider" />
              <button className="share-menu-item" onClick={() => {
                setShowShareMenu(false);
                setShowManageAccess(true);
              }}>
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
                  <circle cx="6" cy="6" r="2.5"/>
                  <path d="M2 14c0-2.5 2-4 4-4s4 1.5 4 4"/>
                  <path d="M11 5l1.5 1.5L15 4"/>
                </svg>
                Manage access
              </button>
            </div>
          )}
        </div>
        <ThemeToggle />

        <NotificationBell />
        <PresenceAvatars
          followClientId={followClientId}
          onFollowToggle={(clientId) => setFollowClientId(followClientId === clientId ? null : clientId)}
        />

        <div className="topbar-status">
          <span className={`dot ${status}`} />
          <span className="topbar-status-text">
            {status === "loading" && "Initializing OCCT kernel..."}
            {status === "computing" && "Computing geometry..."}
            {status === "error" && (errorMsg || "Error")}
            {status === "idle" && "Ready"}
          </span>
        </div>
      </div>

      {/* ── Main Content ────────────────────────────────────── */}
      <div className="main-content">
        {/* Sidebar: Script + Params / Versions */}
        <div className="sidebar">
          <div className="sidebar-tabs">
            {([
              ["design",   "Design",   "M3 5l5-3 5 3v6l-5 3-5-3V5zM3 5l5 3 5-3M8 8v6"],
              ["script",   "Script",   "M5 3l6 5-6 5"],
              ["ai",       "AI",       "M3 8a5 5 0 1110 0 5 5 0 01-10 0zM6 7h4M6 9h3"],
              ["versions", "Versions", "M8 2.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM8 10.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM12 6.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM8 5.5v5M8 5.5c2 0 4 1 4 2.5"],
              ...(docType === "assembly" ? [["motion", "Motion", "M3 8a5 5 0 1110 0 5 5 0 01-10 0zM8 5v3l2 1.5"]] : []),
              ["release",  "Release",  "M8 2v4M5 4l3 3 3-3M3 9a5 5 0 0010 0"],
              ["bom",      "BOM",      "M2 2h12a1 1 0 011 1v10a1 1 0 01-1 1H2a1 1 0 01-1-1V3a1 1 0 011-1zM2 6h12M6 6v8"],
              ["eco",      "ECOs",     "M3 3h10v4H3zM5 7v6M11 7v6M3 13h10"],
            ] as [SidebarTab, string, string][]).map(([key, label, path]) => (
              <button
                key={key}
                className={`sidebar-tab ${sidebarTab === key ? "active" : ""}`}
                onClick={() => setSidebarTab(key)}
                title={label}
              >
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><path d={path}/></svg>
              </button>
            ))}
          </div>
          {sidebarTab === "design" && (
            <>
            <DesignPanel
              features={featureStore.features}
              selectedId={featureStore.selectedId}
              selectedFeature={featureStore.selectedFeature}
              onAdd={featureStore.addFeature}
              onAddExtrude={featureStore.addExtrudeFeature}
              onAddSketch={featureStore.addSketchFeature}
              onAddLoft={featureStore.addLoftFeature}
              onAddRevolve={featureStore.addRevolveFeature}
              onAddSweep={featureStore.addSweepFeature}
              onAddBoolean={featureStore.addBooleanFeature}
              onAddModifier={featureStore.addModifierFeature}
              onAddTransform={featureStore.addTransformFeature}
              isAssembly={docType === "assembly"}
              onAddImportedPart={docType === "assembly" ? () => setShowPartPicker(true) : undefined}
              onStartMate={docType === "assembly" ? () => { setMateMode(!mateMode); setMateSource(null); } : undefined}
              onAddAssemblyPattern={docType === "assembly" ? featureStore.addAssemblyPattern : undefined}
              onAddSheetMetalBase={featureStore.addSheetMetalBase}
              onAddSheetMetalFlange={featureStore.addSheetMetalFlange}
              onAddSheetMetalCornerRelief={featureStore.addSheetMetalCornerRelief}
              onCheckInterference={docType === "assembly" ? async () => {
                const script = getCurrentScript();
                if (!script || !runnerRef.current) return;
                showToast("Checking interference...", "info");
                const result = await runnerRef.current.checkInterference(script, script);
                if (result.interferes) {
                  showToast(`Interference detected (${result.vertexCount} overlap vertices)`, "error");
                } else {
                  showToast("No interference detected", "success");
                }
              } : undefined}
              mateMode={mateMode}
              onSelect={featureStore.selectFeature}
              onToggle={featureStore.toggleFeature}
              onDelete={featureStore.deleteFeature}
              onMove={featureStore.moveFeature}
              onUpdate={featureStore.updateFeature}
              onRemoveMate={featureStore.removeMate}
              onUpdateMate={featureStore.updateMate}
              onUpdateSketchCommands={featureStore.updateSketchCommands}
            />
            {featureStore.features.some((f) => f.type === "sheetMetalBase") && (
              <BendTablePanel
                features={featureStore.features}
                onShowFlatPattern={(script) => {
                  handleExecute(script);
                  showToast("Flat pattern preview", "info");
                }}
              />
            )}
            </>
          )}
          {sidebarTab === "script" && (
            <>
              {editMode === "script" && (
                <ParamPanel params={paramsMeta} values={paramValues} onChange={handleParamChange} />
              )}
              <div className="editor-body">
                {diffState ? (
                  <ErrorBoundary>
                    <DiffViewer
                      original={diffState.original}
                      modified={diffState.modified}
                      originalLabel={diffState.originalLabel}
                      modifiedLabel={diffState.modifiedLabel}
                      onClose={() => setDiffState(null)}
                    />
                  </ErrorBoundary>
                ) : (
                  <ErrorBoundary>
                    {initialScript !== null && (
                      <ScriptEditor
                        ref={scriptEditorRef}
                        onExecute={handleExecute}
                        savedScript={initialScript}
                        readOnly={editMode === "gui"}
                        banner={editMode === "gui" ? "Generated from feature tree" : undefined}
                        errorMsg={status === "error" ? errorMsg : undefined}
                      />
                    )}
                    {editMode === "gui" && (
                      <div className="detach-to-script">
                        <button className="detach-btn" onClick={handleDetachToScript}>
                          Detach to script mode
                        </button>
                      </div>
                    )}
                  </ErrorBoundary>
                )}
              </div>
              {status === "error" && errorMsg && (
                <div className="script-error-panel">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <span className="script-error-category">{
                      /^OCCT geometry/.test(errorMsg) ? "Geometry" :
                      /^SyntaxError/.test(errorMsg) ? "Syntax" : "Runtime"
                    }</span>
                    {(() => {
                      const match = errorMsg.match(/(?:<anonymous>|at main|at eval):(\d+)(?::(\d+))?/);
                      if (match) {
                        const line = parseInt(match[1], 10);
                        const col = match[2] ? parseInt(match[2], 10) : 1;
                        return (
                          <button
                            className="jump-to-line-btn"
                            onClick={() => scriptEditorRef.current?.jumpToLine(line, col)}
                          >
                            Jump to line {line}
                          </button>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  <span className="script-error-msg">{errorMsg}</span>
                </div>
              )}
            </>
          )}
          {sidebarTab === "versions" && (
            <VersionPanel
              getCurrentScript={getCurrentScript}
              getCurrentFeatures={getCurrentFeatures}
              onCheckout={handleVersionCheckout}
              onCompare={handleCompare}
            />
          )}
          {sidebarTab === "ai" && (
            <AiAdvisorPanel
              currentScript={getCurrentScript()}
              onApplyScript={(script) => {
                ydoc.transact(() => {
                  yText.delete(0, yText.length);
                  yText.insert(0, script);
                });
                handleExecute(script);
                showToast("Script applied from AI", "success");
              }}
            />
          )}
          {sidebarTab === "motion" && (
            <MotionStudyPanel
              features={featureStore.features}
              onUpdateMate={featureStore.updateMate}
            />
          )}
          {sidebarTab === "release" && (
            <ReleasePanel documentId={documentId} />
          )}
          {sidebarTab === "bom" && (
            <BOMPanel docId={documentId} docType={docType} />
          )}
          {sidebarTab === "eco" && (
            <ECOPanel />
          )}
        </div>

        {/* Viewport or Drawing Canvas */}
        {showDrawing ? (
          <ErrorBoundary>
            <DrawingCanvas
              runner={runnerRef.current}
              docName={documentName}
              partNumber={docPartNumber}
              revision={docRevision}
            />
          </ErrorBoundary>
        ) : (
          <div
            className="viewport-container"
            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragOver(false);
              const file = e.dataTransfer.files[0];
              if (file && (file.name.toLowerCase().endsWith(".step") || file.name.toLowerCase().endsWith(".stp"))) {
                handleStepFile(file);
              }
            }}
          >
            <ErrorBoundary fallback={(err, reset) => (
              <div className="viewport-fallback">
                <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
                  <path d="M8 16l16-10 16 10v16l-16 10-16-10V16z" />
                  <path d="M8 16l16 10 16-10M24 26v16" />
                </svg>
                <span>{err.message.includes("WebGL") ? "3D viewport requires GPU — geometry computed in background" : err.message}</span>
                <button onClick={reset}>Retry</button>
              </div>
            )}>
              <Viewport
                ref={viewportRef} mesh={mesh} featureMeshes={featureMeshes}
                onMeshSelect={handleMeshSelect} onMeshHit={handleMeshHit}
                selectedFeatureId={featureStore.selectedId} mateMode={mateMode}
                sectionEnabled={sectionEnabled} sectionAxis={sectionAxis} sectionPosition={sectionPosition}
                explodeScale={explodeScale}
                explodeOffsets={explodeOffsets}
                featureMaterials={featureMaterials}
                followCamera={followClientId ? getCameraState(followClientId) : null}
                onCameraMove={setCameraState}
              />
            </ErrorBoundary>

            {annotationStore.annotations.length > 0 && (
              <Annotation3DOverlay
                annotations={annotationStore.annotations}
                onResolve={annotationStore.resolveAnnotation}
                onDelete={annotationStore.deleteAnnotation}
              />
            )}

            {commentMode && !mateMode && (
              <div className="viewport-comment-hint">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="12" height="12">
                  <path d="M3 3h10a1 1 0 011 1v6a1 1 0 01-1 1H6l-3 3V4a1 1 0 011-1z"/>
                </svg>
                Click on geometry to place a comment
                <button className="mate-cancel-btn" onClick={() => setCommentMode(false)}>Done</button>
              </div>
            )}

            {mateMode && (
              <div className="viewport-mate-hint">
                <span>{mateSource ? "Click target face" : "Click source face on fixed part"}</span>
                {mateSource && (
                  <div className="mate-type-selector">
                    {([
                      ["faceToFace", "Face", "M3 12L8 3l5 9H3z"],
                      ["coincident", "Point", "M8 4a4 4 0 100 8 4 4 0 000-8z"],
                      ["fastened", "Lock", "M5 8V6a3 3 0 016 0v2M4 8h8a1 1 0 011 1v4a1 1 0 01-1 1H4a1 1 0 01-1-1V9a1 1 0 011-1z"],
                      ["revolute", "Hinge", "M8 2a6 6 0 110 12A6 6 0 018 2zM8 5v3l2.5 1.5"],
                      ["slider", "Slide", "M2 8h12M5 5l-3 3 3 3M11 5l3 3-3 3"],
                    ] as [MateType, string, string][]).map(([type, label, path]) => (
                      <button
                        key={type}
                        className={`mate-type-btn ${mateType === type ? "active" : ""}`}
                        onClick={() => setMateType(type)}
                        aria-pressed={mateType === type}
                        aria-label={`${label} mate`}
                      >
                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" width="14" height="14">
                          <path d={path}/>
                        </svg>
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>
                )}
                <button className="mate-cancel-btn" onClick={() => { setMateMode(false); setMateSource(null); }}>Cancel</button>
              </div>
            )}
            {featureStore.selectedFeature && !mateMode && (
              <div className="viewport-selection-info">
                <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12L8 3l5 9H3z"/></svg>
                {featureStore.selectedFeature.name}
              </div>
            )}

            <div className={`drop-overlay ${isDragOver ? "active" : ""}`}>
              <span className="drop-overlay-text">Drop STEP file to import</span>
            </div>

            <div className="viewport-hud">
              <div className="viewport-hud-item">
                <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="8" cy="8" r="2"/><path d="M8 2v2M8 12v2M2 8h2M12 8h2"/>
                </svg>
                {vertexCount.toLocaleString()} verts
              </div>
              <div className="viewport-hud-item">
                <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M3 12L8 3l5 9H3z"/>
                </svg>
                {faceCount.toLocaleString()} tris
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Status Bar ──────────────────────────────────────── */}
      <div className="statusbar">
        <div className="statusbar-section">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><path d="M8 2v12M2 8h12"/></svg>
          replicad + OCCT
        </div>
        <div className="statusbar-section">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><circle cx="8" cy="8" r="5"/><path d="M8 5v3l2 1"/></svg>
          {savedAt ? `Saved ${savedAt}` : "Unsaved"}
        </div>
        <div className="statusbar-spacer" />
        <div className="statusbar-section statusbar-accent">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"><path d="M2 8c0-3.3 2.7-6 6-6s6 2.7 6 6-2.7 6-6 6"/><path d="M5 8l2 2 4-4"/></svg>
          Phase 6 — Change Management
        </div>
      </div>

      {showPartPicker && (
        <PartPicker
          assemblyDocId={documentId}
          onSelect={handlePartPicked}
          onClose={() => setShowPartPicker(false)}
        />
      )}
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
      {showManageAccess && (
        <ManageAccess documentId={documentId} onClose={() => setShowManageAccess(false)} />
      )}
    </div>
  );
}
